import documentsSvc, {
  IOnboardingDocuments,
} from '@efex-services/documents/documentsSvc';
import {
  OnboardingDocumentTypeIds,
  OnboardingUsaDocumentTypeIds,
} from '@efex-services/documents/types';
import { UseApiCallProps } from '@pages/lending/types';
import { selectBusiness } from '@pages/profile/store/selectors';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useApiCall } from 'use-api-call';
import {
  IAssignStakeholderDocuments,
  UseStakeholderFileServiceProps,
  UseStakeholderFileServiceReturn,
} from './types/useFileService.type';

const useStakeholderFileService = ({
  getStakeHolder,
}: UseStakeholderFileServiceProps): UseStakeholderFileServiceReturn => {
  const company = useSelector(selectBusiness);

  const [uploadedFiles, setUploadedFiles] = useState<IOnboardingDocuments[]>(
    []
  );

  const {
    loading: uploadingDocuments,
    data: uploadData,
    error: errorUploadDocuments,
    invoke: uploadStakeholderOnboardingDocument,
  }: UseApiCallProps<
    IOnboardingDocuments[],
    {
      currentDocuments: File[];
      name: OnboardingDocumentTypeIds;
    }
  > = useApiCall(({ currentDocuments, name }) =>
    documentsSvc.uploadStakeholderOnboardingDocument(
      company?.enterpriseId as unknown as number,
      {
        documentTypeId:
          (OnboardingDocumentTypeIds[
            name
          ] as unknown as OnboardingDocumentTypeIds) ||
          (OnboardingUsaDocumentTypeIds[
            name
          ] as unknown as OnboardingDocumentTypeIds),
        totalDocuments: currentDocuments.length,
        documents: currentDocuments as unknown as FormData,
      }
    )
  );

  const { error: errorDelete, invoke: deleteDocument } = useApiCall(
    ({ documentId }: { documentId: number }) =>
      documentsSvc.deleteOnboardingDocuments(
        company?.enterpriseId as unknown as number,
        documentId
      )
  );

  const {
    data: assignedDocuments,
    loading: loadingAssignDocuments,
    invoke: asignStakeholderDocuments,
  } = useApiCall(({ metadata, documentId }: IAssignStakeholderDocuments) =>
    documentsSvc.asignStakeholderDocuments({
      metadata,
      enterpriseId: company?.enterpriseId as unknown as number,
      documentId,
    })
  );

  const uploadFilesFromEvent = (
    files: FileList,
    name: OnboardingDocumentTypeIds
  ) => {
    const filesArray: File[] = [];
    for (let index = 0; index <= files.length - 1; index += 1) {
      filesArray[index] = files.item(index) as unknown as File;
    }
    uploadStakeholderOnboardingDocument({
      currentDocuments: filesArray,
      name,
    });
  };

  const handleChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files, name } = event.target;
    if (files && files.length) {
      uploadFilesFromEvent(files, name as unknown as OnboardingDocumentTypeIds);
    }
  };

  const handleDropChangeFile = (files: FileList, name: string) => {
    if (files && files.length) {
      uploadFilesFromEvent(files, name as unknown as OnboardingDocumentTypeIds);
    }
  };

  const handleDeleteFile = async (id: string | number) => {
    try {
      await deleteDocument({ documentId: id });
      setUploadedFiles((prevFiles) =>
        prevFiles.filter((file) => file.id !== id)
      );
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const assignDocuments = async (
    stakeholderId: number,
    metadata: Record<string, string | undefined> | null
  ) => {
    const validMetadataIds = [
      OnboardingDocumentTypeIds.personalIdentification,
      OnboardingDocumentTypeIds.personalIdentificationBack,
      OnboardingUsaDocumentTypeIds.driversLicence,
      OnboardingUsaDocumentTypeIds.driversLicenceBack,
      OnboardingUsaDocumentTypeIds.passport,
      OnboardingUsaDocumentTypeIds.personalId,
      OnboardingUsaDocumentTypeIds.personalIdBack,
    ];
    if (!stakeholderId) return;
    const documents = uploadedFiles.filter((document) =>
      validMetadataIds.includes(document.document.type.id)
    );
    documents.forEach((document) => {
      asignStakeholderDocuments({
        metadata: metadata || undefined,
        documentId: document.id,
      });
    });
  };

  const resetUploadedFiles = () => {
    setUploadedFiles([]);
  };

  useEffect(() => {
    if (uploadData && !uploadingDocuments) {
      setUploadedFiles((prevFiles) => [...prevFiles, ...uploadData]);
    }
  }, [uploadData, uploadingDocuments]);

  useEffect(() => {
    if (assignedDocuments && !loadingAssignDocuments) {
      getStakeHolder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedDocuments, loadingAssignDocuments]);

  return {
    uploadedFiles,
    errorUploadDocuments,
    errorDelete,
    handleChangeFile,
    handleDropChangeFile,
    handleDeleteFile,
    assignDocuments,
    resetUploadedFiles,
    uploadStakeholderOnboardingDocument,
  };
};

export default useStakeholderFileService;
